import { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Video, Send, Link, Eye, CheckCircle, Clock, AlertTriangle,
  Users, Truck, Play, Mail, Copy, RefreshCw, Plus, Search,
  FileVideo, History, ExternalLink, Home, ArrowLeft, XCircle,
  Timer, Percent, AlertOctagon
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrainingPage() {
  const [drivers, setDrivers] = useState([]);
  const [videos, setVideos] = useState([]);
  const [trainingLinks, setTrainingLinks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [selectedVideo, setSelectedVideo] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [showAddVideoDialog, setShowAddVideoDialog] = useState(false);
  const [newVideo, setNewVideo] = useState({ title: '', description: '', url: '', duration_seconds: 60 });
  const [searchTerm, setSearchTerm] = useState('');
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [driversRes, videosRes, linksRes] = await Promise.all([
        axios.get(`${API}/fleet/drivers`, { withCredentials: true }),
        axios.get(`${API}/training/videos`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/training/links`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      const driversData = driversRes.data?.drivers || driversRes.data || [];
      setDrivers(driversData);
      setVideos(videosRes.data || []);
      setTrainingLinks(linksRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load training data');
    } finally {
      setIsLoading(false);
    }
  };

  const generateLink = async () => {
    if (!selectedDriver || !selectedVideo) {
      toast.error('Please select both driver and video');
      return;
    }
    
    try {
      const res = await axios.post(`${API}/training/generate-link`, {
        driver_id: selectedDriver,
        video_id: selectedVideo
      }, { withCredentials: true });
      
      setGeneratedLink(res.data.link);
      setShowSendDialog(true);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate link');
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(generatedLink);
    toast.success('Link copied to clipboard!');
  };

  const sendEmail = async () => {
    setIsSending(true);
    try {
      const driver = drivers.find(d => d.driver_id === selectedDriver);
      await axios.post(`${API}/training/send-email`, {
        driver_id: selectedDriver,
        link: generatedLink
      }, { withCredentials: true });
      
      toast.success(`Email sent to ${driver?.name || 'driver'}!`);
      setShowSendDialog(false);
      setSelectedDriver('');
      setSelectedVideo('');
      setGeneratedLink('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email. Is email configured?');
    } finally {
      setIsSending(false);
    }
  };

  const addVideo = async () => {
    if (!newVideo.title || !newVideo.url) {
      toast.error('Title and URL are required');
      return;
    }
    
    try {
      await axios.post(`${API}/training/videos`, newVideo, { withCredentials: true });
      toast.success('Video added successfully!');
      setShowAddVideoDialog(false);
      setNewVideo({ title: '', description: '', url: '', duration_seconds: 60 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add video');
    }
  };

  const formatWatchTime = (seconds) => {
    if (!seconds && seconds !== 0) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusBadge = (link) => {
    const status = link.status;
    const progress = link.watch_progress || 0;
    const closedEarly = link.closed_early;
    
    if (status === 'watched' && progress >= 90) {
      return <Badge className="bg-green-500/20 text-green-400 border-green-500/30"><CheckCircle className="w-3 h-3 mr-1" />Completed {progress}%</Badge>;
    } else if (closedEarly) {
      return <Badge className="bg-red-500/20 text-red-400 border-red-500/30"><XCircle className="w-3 h-3 mr-1" />Closed Early ({progress}%)</Badge>;
    } else if (status === 'opened') {
      return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30"><Eye className="w-3 h-3 mr-1" />Watching ({progress}%)</Badge>;
    } else if (status === 'sent') {
      return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30"><Send className="w-3 h-3 mr-1" />Sent</Badge>;
    }
    return <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
  };

  const filteredLinks = trainingLinks.filter(link => {
    const driver = drivers.find(d => d.driver_id === link.driver_id);
    const video = videos.find(v => v.video_id === link.video_id);
    const searchLower = searchTerm.toLowerCase();
    return (
      driver?.name?.toLowerCase().includes(searchLower) ||
      video?.title?.toLowerCase().includes(searchLower) ||
      link.status?.toLowerCase().includes(searchLower)
    );
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border/40 bg-card/30 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <RouterLink to="/hub">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </RouterLink>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Video className="w-6 h-6 text-primary" />
                Driver Training
              </h1>
              <p className="text-sm text-muted-foreground">Send videos & track progress</p>
            </div>
          </div>
          <RouterLink to="/hub">
            <Button variant="outline" size="sm">
              <Home className="w-4 h-4 mr-2" />
              Back to Hub
            </Button>
          </RouterLink>
        </div>
      </div>
      
      <div className="container mx-auto p-4 lg:p-6 space-y-6">
        <Tabs defaultValue="send" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 lg:w-[400px]">
            <TabsTrigger value="send" className="flex items-center gap-2">
              <Send className="w-4 h-4" />
              Send
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <History className="w-4 h-4" />
              History
            </TabsTrigger>
            <TabsTrigger value="videos" className="flex items-center gap-2">
              <FileVideo className="w-4 h-4" />
              Videos
            </TabsTrigger>
          </TabsList>

          {/* SEND VIDEO TAB */}
          <TabsContent value="send" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Step 1: Select Driver */}
              <Card className="bg-card/50 backdrop-blur-sm border-border/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Users className="w-5 h-5 text-blue-400" />
                    Step 1: Select Driver
                  </CardTitle>
                  <CardDescription>Choose which driver needs training</CardDescription>
                </CardHeader>
                <CardContent>
                  <Select value={selectedDriver} onValueChange={setSelectedDriver}>
                    <SelectTrigger data-testid="select-driver">
                      <SelectValue placeholder="Select a driver..." />
                    </SelectTrigger>
                    <SelectContent>
                      {drivers.map((driver) => (
                        <SelectItem key={driver.driver_id} value={driver.driver_id}>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{driver.name}</span>
                            {driver.assigned_truck && (
                              <Badge variant="outline" className="text-xs">
                                {driver.assigned_truck}
                              </Badge>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {selectedDriver && (
                    <div className="mt-4 p-3 bg-secondary/50 rounded-lg">
                      {(() => {
                        const driver = drivers.find(d => d.driver_id === selectedDriver);
                        return driver ? (
                          <div className="space-y-1 text-sm">
                            <p><span className="text-muted-foreground">Name:</span> {driver.name}</p>
                            <p><span className="text-muted-foreground">Email:</span> {driver.email || 'Not set'}</p>
                            <p><span className="text-muted-foreground">Phone:</span> {driver.phone_number || 'Not set'}</p>
                            <p><span className="text-muted-foreground">Truck:</span> {driver.assigned_truck || 'Unassigned'}</p>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Step 2: Select Video */}
              <Card className="bg-card/50 backdrop-blur-sm border-border/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Video className="w-5 h-5 text-purple-400" />
                    Step 2: Select Training Video
                  </CardTitle>
                  <CardDescription>Choose which video to send</CardDescription>
                </CardHeader>
                <CardContent>
                  <Select value={selectedVideo} onValueChange={setSelectedVideo}>
                    <SelectTrigger data-testid="select-video">
                      <SelectValue placeholder="Select a video..." />
                    </SelectTrigger>
                    <SelectContent>
                      {videos.map((video) => (
                        <SelectItem key={video.video_id} value={video.video_id}>
                          <div className="flex items-center gap-2">
                            <Play className="w-4 h-4 text-red-400" />
                            <span>{video.title}</span>
                            {video.duration_seconds && (
                              <span className="text-xs text-muted-foreground">
                                ({formatWatchTime(video.duration_seconds)})
                              </span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {selectedVideo && (
                    <div className="mt-4 p-3 bg-secondary/50 rounded-lg">
                      {(() => {
                        const video = videos.find(v => v.video_id === selectedVideo);
                        return video ? (
                          <div className="space-y-2">
                            <p className="font-medium">{video.title}</p>
                            <p className="text-sm text-muted-foreground">{video.description}</p>
                            <a 
                              href={video.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-blue-400 hover:underline flex items-center gap-1"
                            >
                              <ExternalLink className="w-3 h-3" />
                              Preview video
                            </a>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Step 3: Generate & Send */}
            <Card className="bg-card/50 backdrop-blur-sm border-border/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Link className="w-5 h-5 text-green-400" />
                  Step 3: Generate Link & Send
                </CardTitle>
                <CardDescription>Create a unique tracking link and send to driver</CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  onClick={generateLink}
                  disabled={!selectedDriver || !selectedVideo}
                  className="w-full sm:w-auto"
                  data-testid="generate-link-btn"
                >
                  <Link className="w-4 h-4 mr-2" />
                  Generate Unique Link
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* HISTORY TAB */}
          <TabsContent value="history" className="space-y-4">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <Card className="bg-card/50 backdrop-blur-sm border-border/50">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">Total Sent</p>
                      <p className="text-2xl font-bold">{trainingLinks.length}</p>
                    </div>
                    <Send className="w-6 h-6 text-blue-400 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-card/50 backdrop-blur-sm border-border/50">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">Opened</p>
                      <p className="text-2xl font-bold text-yellow-400">
                        {trainingLinks.filter(l => l.status === 'opened' || l.status === 'watched').length}
                      </p>
                    </div>
                    <Eye className="w-6 h-6 text-yellow-400 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-card/50 backdrop-blur-sm border-border/50">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">Completed</p>
                      <p className="text-2xl font-bold text-green-400">
                        {trainingLinks.filter(l => l.status === 'watched' && l.watch_progress >= 90).length}
                      </p>
                    </div>
                    <CheckCircle className="w-6 h-6 text-green-400 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-card/50 backdrop-blur-sm border-border/50">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">Closed Early</p>
                      <p className="text-2xl font-bold text-red-400">
                        {trainingLinks.filter(l => l.closed_early).length}
                      </p>
                    </div>
                    <XCircle className="w-6 h-6 text-red-400 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-card/50 backdrop-blur-sm border-border/50">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">Pending</p>
                      <p className="text-2xl font-bold text-gray-400">
                        {trainingLinks.filter(l => l.status === 'sent' || l.status === 'pending').length}
                      </p>
                    </div>
                    <Clock className="w-6 h-6 text-gray-400 opacity-50" />
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-card/50 backdrop-blur-sm border-border/50">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <CardTitle>Training History</CardTitle>
                    <CardDescription>Detailed tracking - seconds watched, completion status</CardDescription>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 w-full sm:w-64"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Driver</TableHead>
                        <TableHead>Video</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>
                          <div className="flex items-center gap-1">
                            <Timer className="w-3 h-3" />
                            Watch Time
                          </div>
                        </TableHead>
                        <TableHead>
                          <div className="flex items-center gap-1">
                            <Percent className="w-3 h-3" />
                            Progress
                          </div>
                        </TableHead>
                        <TableHead>Sent</TableHead>
                        <TableHead>Opened</TableHead>
                        <TableHead>Completed</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredLinks.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                            No training links sent yet
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredLinks.map((link) => {
                          const driver = drivers.find(d => d.driver_id === link.driver_id);
                          const video = videos.find(v => v.video_id === link.video_id);
                          return (
                            <TableRow key={link.link_id} className={link.closed_early ? 'bg-red-500/5' : ''}>
                              <TableCell className="font-medium">{driver?.name || 'Unknown'}</TableCell>
                              <TableCell>{video?.title || 'Unknown'}</TableCell>
                              <TableCell>{getStatusBadge(link)}</TableCell>
                              <TableCell className="font-mono text-sm">
                                {formatWatchTime(link.seconds_watched || 0)}
                                {video?.duration_seconds && (
                                  <span className="text-muted-foreground">
                                    /{formatWatchTime(video.duration_seconds)}
                                  </span>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <div className="w-16 h-2 bg-secondary rounded-full overflow-hidden">
                                    <div 
                                      className={`h-full ${link.watch_progress >= 90 ? 'bg-green-500' : link.closed_early ? 'bg-red-500' : 'bg-yellow-500'}`}
                                      style={{ width: `${link.watch_progress || 0}%` }}
                                    />
                                  </div>
                                  <span className="text-xs">{link.watch_progress || 0}%</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground">
                                {link.sent_at ? new Date(link.sent_at).toLocaleString() : '-'}
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground">
                                {link.opened_at ? new Date(link.opened_at).toLocaleString() : '-'}
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground">
                                {link.completed_at ? new Date(link.completed_at).toLocaleString() : '-'}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* VIDEOS TAB */}
          <TabsContent value="videos" className="space-y-4">
            <Card className="bg-card/50 backdrop-blur-sm border-border/50">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <CardTitle>Training Videos</CardTitle>
                    <CardDescription>Manage your training video library</CardDescription>
                  </div>
                  <Button onClick={() => setShowAddVideoDialog(true)} data-testid="add-video-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Video
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {videos.length === 0 ? (
                    <div className="col-span-full text-center py-8 text-muted-foreground">
                      No videos added yet. Click "Add Video" to get started.
                    </div>
                  ) : (
                    videos.map((video) => (
                      <Card key={video.video_id} className="bg-secondary/30">
                        <CardContent className="pt-4">
                          <div className="flex items-start gap-3">
                            <div className="w-12 h-12 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
                              <Play className="w-6 h-6 text-red-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium truncate">{video.title}</h4>
                              <p className="text-sm text-muted-foreground line-clamp-2">{video.description}</p>
                              {video.duration_seconds && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Duration: {formatWatchTime(video.duration_seconds)}
                                </p>
                              )}
                              <a 
                                href={video.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-xs text-blue-400 hover:underline flex items-center gap-1 mt-2"
                              >
                                <ExternalLink className="w-3 h-3" />
                                Open video
                              </a>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Send Email Dialog */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              Link Generated!
            </DialogTitle>
            <DialogDescription>
              Send this unique tracking link to the driver
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-secondary rounded-lg">
              <Label className="text-xs text-muted-foreground">Unique Link</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input value={generatedLink} readOnly className="text-xs" />
                <Button variant="outline" size="icon" onClick={copyLink}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <p className="text-sm flex items-center gap-2">
                <AlertOctagon className="w-4 h-4 text-yellow-400" />
                <span className="text-yellow-400 font-medium">Anti-cheat enabled:</span>
              </p>
              <ul className="text-xs text-muted-foreground mt-2 space-y-1 ml-6">
                <li>• Video cannot be skipped forward</li>
                <li>• Exact seconds watched are tracked</li>
                <li>• Early window close is detected</li>
              </ul>
            </div>
            
            {(() => {
              const driver = drivers.find(d => d.driver_id === selectedDriver);
              return driver?.email ? (
                <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <p className="text-sm">
                    <Mail className="w-4 h-4 inline mr-2 text-green-400" />
                    Will send to: <strong>{driver.email}</strong>
                  </p>
                </div>
              ) : (
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                  <p className="text-sm text-yellow-400">
                    <AlertTriangle className="w-4 h-4 inline mr-2" />
                    Driver has no email. Copy link and send manually.
                  </p>
                </div>
              );
            })()}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={copyLink} className="w-full sm:w-auto">
              <Copy className="w-4 h-4 mr-2" />
              Copy Link
            </Button>
            <Button 
              onClick={sendEmail} 
              disabled={isSending || !drivers.find(d => d.driver_id === selectedDriver)?.email}
              className="w-full sm:w-auto"
              data-testid="send-email-btn"
            >
              {isSending ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Video Dialog */}
      <Dialog open={showAddVideoDialog} onOpenChange={setShowAddVideoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Training Video</DialogTitle>
            <DialogDescription>
              Add a YouTube or video URL to your training library
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Video Title *</Label>
              <Input
                placeholder="e.g., Safe Braking Techniques"
                value={newVideo.title}
                onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                placeholder="Brief description of the video content"
                value={newVideo.description}
                onChange={(e) => setNewVideo({ ...newVideo, description: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Video URL *</Label>
              <Input
                placeholder="https://youtube.com/watch?v=..."
                value={newVideo.url}
                onChange={(e) => setNewVideo({ ...newVideo, url: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Paste a YouTube, Vimeo, or direct video URL
              </p>
            </div>
            <div className="space-y-2">
              <Label>Video Duration (seconds) *</Label>
              <Input
                type="number"
                placeholder="60"
                value={newVideo.duration_seconds}
                onChange={(e) => setNewVideo({ ...newVideo, duration_seconds: parseInt(e.target.value) || 60 })}
              />
              <p className="text-xs text-muted-foreground">
                Used to calculate completion percentage
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddVideoDialog(false)}>Cancel</Button>
            <Button onClick={addVideo} data-testid="save-video-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Video
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
